import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Award, ChevronUp, Check, AlertCircle, Printer, Camera, ChevronsUpDown, X, Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useUnsavedChangesGuard } from "@/hooks/useUnsavedChangesGuard";
import { toast } from "sonner";
import PrintChecklistModal from "@/components/PrintChecklistModal";
import ScanChecklistModal from "@/components/ScanChecklistModal";
import CoachNotesSummary from "@/components/CoachNotesSummary";
import { SetTargetLevelForm } from "@/components/SetTargetLevelForm";
import { LevelJourney } from "@/components/LevelJourney";
import { ConfirmChangeDialog } from "@/components/ConfirmChangeDialog";

const STATUS_META = {
  competent: { label: "Competent", color: "#CCFF00", textColor: "#0A0A0A" },
  developing: { label: "Developing", color: "#FF4500", textColor: "#0A0A0A" },
  not_yet: { label: "Not Yet", color: "#525252", textColor: "#E5E5E5" },
  not_assessed: { label: "Not Assessed", color: "#141414", textColor: "#A3A3A3" },
  flagged: { label: "Flagged", color: "#FF9F1C", textColor: "#0A0A0A" },
  waived: { label: "Waived", color: "#60A5FA", textColor: "#0A0A0A" },
};

const ALLOWED = ["2.5", "3.0", "3.5", "4.0", "4.5"];
// Old confirm-starting-level / adjust-level UI is superseded by SetTargetLevelForm.
const LEGACY_CONFIRM = false;
const LEVEL_LABELS = { "2.5": "Beginner", "3.0": "Recreational", "3.5": "Intermediate", "4.0": "Strong Int.", "4.5": "Advanced" };

export default function CoachAssessmentPanel({ studentId, onLevelChange }) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [openNotes, setOpenNotes] = useState({}); // skill_id -> bool
  const [draftNotes, setDraftNotes] = useState({}); // skill_id -> string
  const [staged, setStaged] = useState({}); // skill_id -> {status?, notes?} awaiting Review & save
  const [reviewOpen, setReviewOpen] = useState(false);
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [noteIntent, setNoteIntent] = useState({}); // skill_id -> status the coach tried to set (drives note label)
  const stagedCount = Object.keys(staged).length;
  const guard = useUnsavedChangesGuard(stagedCount > 0, () => { setStaged({}); setDraftNotes({}); setOpenNotes({}); });
  const [printOpen, setPrintOpen] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [confirmPick, setConfirmPick] = useState("");
  const [confirmNotes, setConfirmNotes] = useState("");   // iter104A — coach's rationale, mandatory on override
  const [shownLevels, setShownLevels] = useState([]); // extra levels the coach ticked on top of current
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustLevel, setAdjustLevel] = useState("");
  const [adjustReason, setAdjustReason] = useState("");
  // Iter92 — Session context header. Shows the upcoming (or most recent) 1:1
  // assessment booking for this student so the coach knows exactly which
  // session they're reviewing on the page. Uses the existing coach booking
  // feed; picks first future booking else last past one, so the header
  // never blanks out.
  const [ctxBooking, setCtxBooking] = useState(null);
  useEffect(() => {
    if (!studentId) return;
    let cancelled = false;
    api.get(`/coach/students/${studentId}/bookings`)
      .then((r) => {
        if (cancelled) return;
        const list = Array.isArray(r.data) ? r.data : [];
        const now = Date.now();
        const upcoming = list
          .filter((b) => b.status !== "cancelled")
          .map((b) => ({ ...b, _t: new Date(b.start).getTime() }));
        const future = upcoming.filter((b) => b._t >= now).sort((a, b) => a._t - b._t)[0];
        const past = upcoming.filter((b) => b._t < now).sort((a, b) => b._t - a._t)[0];
        setCtxBooking(future || past || null);
      })
      .catch(() => { if (!cancelled) setCtxBooking(null); });
    return () => { cancelled = true; };
  }, [studentId]);

  const load = async (opts = {}) => {
    try {
      const extra = (opts.levels ?? shownLevels).filter((lv) => lv && ALLOWED.includes(lv));
      const q = extra.length ? `?levels=${encodeURIComponent(extra.join(","))}` : "";
      const { data } = await api.get(`/coach/players/${studentId}/assessment-level${q}`);
      setState(data);
      const seed = {};
      const skills = data?.assessment_level?.skills || {};
      Object.entries(skills).forEach(([sid, entry]) => {
        seed[sid] = entry?.notes || "";
      });
      setDraftNotes(seed);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load assessment level");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (studentId) load({ levels: [] }); }, [studentId]);

  // Hoisted above early returns so hooks order stays stable across renders.
  const stateAl = state?.assessment_level;
  const stateTemplate = state?.template;
  const stateTemplatesByLevel = state?.templates_by_level;
  const visibleLevels = useMemo(() => {
    const cur = stateAl?.current;
    if (!cur) return [];
    const set = new Set([cur, ...shownLevels]);
    return ALLOWED.filter((lv) => set.has(lv));
  }, [stateAl, shownLevels]);
  const combinedTemplates = useMemo(() => {
    const acc = {};
    const cur = stateAl?.current;
    if (stateTemplate && cur) acc[cur] = stateTemplate;
    Object.entries(stateTemplatesByLevel || {}).forEach(([lv, tpl]) => { acc[lv] = tpl; });
    return acc;
  }, [stateTemplate, stateTemplatesByLevel, stateAl]);

  // A11y: close on Escape + focus the reason textarea on open so keyboard
  // users don't have to tab into the modal from scratch. Hoisted above the
  // early returns so React hook ordering stays stable across renders.
  useEffect(() => {
    if (!adjustOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !busy) {
        e.stopPropagation();
        setAdjustOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    const t = setTimeout(() => {
      const el = document.querySelector('[data-testid="adjust-reason-input"]');
      if (el) el.focus();
    }, 60);
    return () => {
      window.removeEventListener("keydown", onKey);
      clearTimeout(t);
    };
  }, [adjustOpen, busy]);

  if (loading) return <div className="card-surface p-6 text-sm text-[#A3A3A3]">Loading assessment…</div>;

  if (!state?.assessment_level) {
    return (
      <div className="card-surface p-5 sm:p-6" data-testid="assessment-panel-empty">
        <SetTargetLevelForm studentId={studentId} playerName={state?.player_name} al={null} highlight
          onSaved={async () => { await load({ levels: [] }); if (onLevelChange) onLevelChange(); }} />
      </div>
    );
  }

  const { assessment_level: al, progress, template, next_level: nxt } = state;
  const allCompetent = progress?.all_resolved;

  const setStatus = (skillId, status) => {
    const note = (draftNotes[skillId] ?? al.skills?.[skillId]?.notes ?? "").trim();
    if (status === "waived" && note.length < 3) {
      setOpenNotes((cur) => ({ ...cur, [skillId]: true }));
      setNoteIntent((cur) => ({ ...cur, [skillId]: "waived" }));
      toast.error("Add a note explaining the waiver first (e.g. 'limited mobility, overhead smash waived'), then tap Waived.");
      return;
    }
    if (status === "flagged" && note.length < 3) {
      setOpenNotes((cur) => ({ ...cur, [skillId]: true }));
      setNoteIntent((cur) => ({ ...cur, [skillId]: "flagged" }));
      toast.error("Add a note explaining why this skill is flagged (e.g. 'struggles with backhand volleys under pressure'), then tap Flagged.");
      return;
    }
    const saved = al.skills?.[skillId] || {};
    setStaged((cur) => {
      const next = { ...cur };
      const entry = { ...(next[skillId] || {}), status };
      if (draftNotes[skillId] !== undefined && draftNotes[skillId] !== (saved.notes || "")) entry.notes = draftNotes[skillId];
      if (entry.status === (saved.status || "not_assessed") && entry.notes === undefined) delete next[skillId];
      else next[skillId] = entry;
      return next;
    });
  };

  const saveNotes = (skillId) => {
    const saved = al.skills?.[skillId] || {};
    const status = staged[skillId]?.status || saved.status || "not_assessed";
    if (["flagged", "waived"].includes(status) && (draftNotes[skillId] ?? "").trim().length < 3) {
      toast.error(`A note is required for a ${status} skill.`);
      return;
    }
    setStaged((cur) => ({ ...cur, [skillId]: { ...(cur[skillId] || {}), status, notes: draftNotes[skillId] ?? "" } }));
    setOpenNotes((cur) => ({ ...cur, [skillId]: false }));
  };

  const skillName = (sid) => {
    for (const t of Object.values(combinedTemplates || {})) {
      for (const g of t?.groups || []) for (const sk of g.skills || []) if (sk.id === sid) return sk.name;
    }
    return sid;
  };
  const stagedLines = Object.entries(staged).map(([sid, ch]) => {
    const saved = al.skills?.[sid] || {};
    const from = STATUS_META[saved.status || "not_assessed"]?.label;
    const to = STATUS_META[ch.status]?.label;
    const parts = [`${skillName(sid)}: ${from === to ? to : `${from} → ${to}`}`];
    if (ch.notes !== undefined && ch.notes !== (saved.notes || "")) parts.push(`note: "${ch.notes}"`);
    return parts.join(" · ");
  });

  const applyStaged = async () => {
    setBusy(true);
    try {
      for (const [sid, ch] of Object.entries(staged)) {
        const saved = al.skills?.[sid] || {};
        await api.post(`/coach/players/${studentId}/assessment-skill`, {
          skill_id: sid, status: ch.status, notes: ch.notes ?? saved.notes ?? "",
        });
      }
      toast.success(`${stagedLines.length} change${stagedLines.length === 1 ? "" : "s"} saved`);
      setStaged({});
      setReviewOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to save changes");
      await load();
    } finally {
      setBusy(false);
    }
  };

  const advance = async () => {
    if (!allCompetent) {
      toast.error("Mark every skill Competent, Flagged or Waived before promoting.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/coach/players/${studentId}/assessment-advance`);
      toast.success(`Promoted from ${data.previous_level} → ${data.current_level}`);
      setPromoteOpen(false);
      await load();
      if (onLevelChange) onLevelChange();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to advance");
    } finally {
      setBusy(false);
    }
  };

  const ALLOWED_LOCAL = ALLOWED;
  const coachConfirmed = Boolean(al?.coach_confirmed_at);
  const hasHistory = (al?.history || []).length > 0;
  const canConfirm = !coachConfirmed && !hasHistory;
  const computedTarget = (() => {
    if (al?.target && al.target !== al.current && ALLOWED_LOCAL.includes(al.target)) return al.target;
    const idx = ALLOWED_LOCAL.indexOf(al?.current);
    if (idx < 0) return al?.current;
    return ALLOWED_LOCAL[Math.min(idx + 1, ALLOWED_LOCAL.length - 1)];
  })();

  // Every group across every visible level (current + coach-selected extras),
  // deduped in DUPR order. Rendered as level-tagged sections so the coach can
  // roll down to lower baselines OR peek at higher targets in one scroll.

  const toggleLevel = (lv) => {
    if (lv === al.current) return;  // current is always shown
    const next = shownLevels.includes(lv)
      ? shownLevels.filter((x) => x !== lv)
      : [...shownLevels, lv];
    setShownLevels(next);
    load({ levels: next });
  };

  const openAdjust = () => {
    setAdjustLevel(al.current || "");
    setAdjustReason("");
    setAdjustOpen(true);
  };

  const submitAdjust = async () => {
    if (!adjustLevel || adjustLevel === al.current) {
      toast.error("Pick a different level");
      return;
    }
    if (adjustReason.trim().length < 2) {
      toast.error("Give a short reason so the audit trail is honest");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/coach/players/${studentId}/adjust-level`, {
        level: adjustLevel,
        reason: adjustReason.trim(),
      });
      toast.success(`Adjusted ${data.previous_level} → ${data.current_level} (${data.direction})`);
      setAdjustOpen(false);
      await load();
      if (onLevelChange) onLevelChange();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Adjust failed");
    } finally {
      setBusy(false);
    }
  };

  const confirmStartingLevel = async () => {
    const picked = confirmPick || al?.self_rated || al?.current;
    if (!picked) { toast.error("Pick a level first"); return; }
    const isOverride = al?.self_rated && picked !== al.self_rated;
    // Iter104A — Explicit acknowledgement. On override, force the coach to
    // provide a short reason. On agree, keep the notes field optional but
    // still send whatever they wrote so the audit trail is rich.
    if (isOverride && !confirmNotes.trim()) {
      toast.error("Add a short note explaining the override — helps the audit trail.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/coach/players/${studentId}/confirm-starting-level`, {
        level: picked,
        notes: confirmNotes.trim() || undefined,
      });
      const override = data.assessment_level.self_rated !== data.assessment_level.starting_level;
      toast.success(override
        ? `Locked in ${picked} (override from self-rated ${data.assessment_level.self_rated})`
        : `Confirmed at self-rated ${picked}`);
      setConfirmNotes("");
      await load();
      if (onLevelChange) onLevelChange();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to confirm");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card-surface p-5 sm:p-6 fade-up" data-testid="coach-assessment-panel">
      {/* Iter92 — Session context header. Tells the coach exactly which
          session this review is for. Uses the closest future assessment
          booking, falling back to the last past one so the label never
          disappears mid-review. */}
      {ctxBooking && (
        <div className="mb-4 pb-3 border-b border-[#262626]" data-testid="assessment-session-context">
          <div className="label-mono text-[10px] text-[#A3A3A3] mb-1">REVIEW FOR</div>
          <div className="text-sm sm:text-base font-semibold text-white">
            {(() => {
              const t = new Date(ctxBooking.start);
              const label = ({
                private_1_1: "1:1 Session",
                pair_1_2: "Pair Session",
                group: "Group Session",
                performance_program: "Performance Program",
              })[ctxBooking.session_type] || (ctxBooking.session_type || "Session");
              const day = t.toLocaleDateString(undefined, { day: "numeric", month: "long" });
              const time = t.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
              return `${label}: ${day} @ ${time} — ${ctxBooking.duration_min || 60} min session`;
            })()}
          </div>
          {ctxBooking.venue && (
            <div className="text-[10px] uppercase tracking-wider text-[#525252] mt-0.5">{ctxBooking.venue}</div>
          )}
        </div>
      )}
      {/* Coach approval banner — shown until the coach locks in the starting
          level at the first assessment. Self-rated level is just a hint. */}
      <SetTargetLevelForm key={`${al.current}-${state?.working_level}`} studentId={studentId} playerName={state?.player_name} al={al}
        workingLevel={state?.working_level} highlight={canConfirm}
        onSaved={async () => { await load(); if (onLevelChange) onLevelChange(); }} />
      {LEGACY_CONFIRM && canConfirm && (
        <div className="mb-6 p-4 border-l-4 border-[#FF4500] bg-[#FF4500]/5" data-testid="confirm-starting-level-panel">
          <div className="flex items-start gap-3 mb-3">
            <AlertCircle className="w-5 h-5 text-[#FF4500] shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="label-mono mb-1" style={{ color: "#FF4500" }}>STARTING LEVEL · AWAITING YOUR CONFIRMATION</div>
              <div className="text-sm text-[#E5E5E5]">
                {state?.player_name || "This player"} self-rated as <span className="text-white font-semibold">{al.self_rated || "—"}</span>. Confirm or override now &mdash; progress tracking starts from this point. <span className="text-[#A3A3A3]">(One-off — can&apos;t be reset later without admin.)</span>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mb-3">
            {ALLOWED.map((lv) => {
              const on = (confirmPick || al.self_rated) === lv;
              const isSelfRated = al.self_rated === lv;
              return (
                <button
                  key={lv}
                  type="button"
                  onClick={() => setConfirmPick(lv)}
                  className="px-3 py-2 text-xs uppercase tracking-wider border transition-colors"
                  style={on
                    ? { borderColor: "#CCFF00", background: "rgba(204,255,0,0.08)", color: "#CCFF00" }
                    : { borderColor: "#262626", color: "#E5E5E5" }}
                  data-testid={`confirm-level-${lv}`}
                >
                  <div className="font-bold">{lv}</div>
                  <div className="text-[9px] text-[#A3A3A3] mt-0.5">
                    {LEVEL_LABELS[lv]}{isSelfRated ? " · self" : ""}
                  </div>
                </button>
              );
            })}
          </div>
          {/* Iter104A — Side-by-side self-vs-coach summary + notes textarea.
              Makes the acknowledge-or-override intent obvious before the coach
              hits Lock In. Notes become mandatory when overriding so the
              audit trail always has a reason. */}
          {(() => {
            const picked = confirmPick || al.self_rated;
            const isOverride = al.self_rated && picked && picked !== al.self_rated;
            return (
              <div className="mb-3 grid sm:grid-cols-2 gap-2" data-testid="confirm-override-summary">
                <div className="border border-[#262626] p-2.5">
                  <div className="label-mono text-[10px] text-[#A3A3A3]">STUDENT SELF-RATED</div>
                  <div className="text-lg font-bold text-white mt-0.5">{al.self_rated || "—"}</div>
                </div>
                <div className="border p-2.5" style={{ borderColor: isOverride ? "#FF4500" : "#CCFF00" }}>
                  <div className="label-mono text-[10px]" style={{ color: isOverride ? "#FF4500" : "#CCFF00" }}>
                    {isOverride ? "COACH OVERRIDE" : "COACH AGREES"}
                  </div>
                  <div className="text-lg font-bold mt-0.5" style={{ color: isOverride ? "#FF4500" : "#CCFF00" }}>{picked || "—"}</div>
                </div>
              </div>
            );
          })()}
          <textarea
            value={confirmNotes}
            onChange={(e) => setConfirmNotes(e.target.value.slice(0, 500))}
            rows={2}
            placeholder={confirmPick && al.self_rated && confirmPick !== al.self_rated
              ? "Reason for the override (required) — e.g. serve consistency below the self-rated band."
              : "Optional note — anything worth logging alongside the confirmation."}
            className="w-full bg-[#0A0A0A] border border-[#262626] px-3 py-2 text-xs mb-3 focus:border-[#CCFF00] focus:outline-none"
            data-testid="confirm-notes"
          />
          <button
            type="button"
            onClick={confirmStartingLevel}
            disabled={busy}
            className="btn-volt px-5 py-2.5 text-xs disabled:opacity-50"
            data-testid="confirm-starting-level-btn"
          >
            <Check className="w-4 h-4 inline mr-1" /> Lock in {confirmPick || al.self_rated || "level"} as starting point
          </button>
        </div>
      )}

      <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 bg-[#CCFF00] flex items-center justify-center shrink-0">
            <Award strokeWidth={2.5} className="w-5 h-5 text-black" />
          </div>
          <div className="min-w-0">
            <div className="label-mono">SKILL ASSESSMENT</div>
            <div className="h-display text-2xl sm:text-3xl tracking-tighter">
              Target level <span className="text-[#CCFF00]" data-testid="assessment-current-level">{al.current}</span>
            </div>
            <div className="text-xs text-[#A3A3A3]">
              Working level <span className="text-white" data-testid="assessment-working-level">{state?.working_level || "—"}</span> · Self-rated {al.self_rated || "—"} · {template?.label}
              {coachConfirmed && <span className="text-[#CCFF00] ml-1">· coach-confirmed</span>}
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="label-mono text-[0.6rem]">PROGRESS</div>
          <div className="h-display text-3xl tracking-tighter" data-testid="assessment-progress-percent">{progress.percent}%</div>
          <div className="text-[10px] text-[#A3A3A3] uppercase tracking-wider mt-1" data-testid="assessment-progress-counts">
            {progress.competent} competent{progress.flagged ? ` · ${progress.flagged} flagged` : ""}{progress.waived ? ` · ${progress.waived} waived` : ""} / {progress.total}
          </div>
          <button
            onClick={() => setPrintOpen(true)}
            className="mt-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider border border-[#262626] hover:border-[#CCFF00] hover:text-[#CCFF00] px-2 py-1"
            data-testid="open-print-checklist"
          >
            <Printer className="w-3 h-3" /> Print checklist
          </button>
          <button
            onClick={() => setScanOpen(true)}
            className="mt-2 ml-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider border border-[#262626] hover:border-[#CCFF00] hover:text-[#CCFF00] px-2 py-1"
            data-testid="open-scan-checklist"
          >
            <Camera className="w-3 h-3" /> Scan sheet
          </button>
        </div>
      </div>
      <PrintChecklistModal
        open={printOpen}
        onClose={() => setPrintOpen(false)}
        defaultLevels={al.current ? [al.current] : ["2.5"]}
        studentName={state?.player_name || ""}
        studentId={studentId}
      />
      <ScanChecklistModal
        open={scanOpen}
        onClose={() => setScanOpen(false)}
        studentId={studentId}
        studentName={state?.player_name || ""}
        currentLevel={al.current || null}
        onSaved={load}
      />

      <div className="h-1.5 bg-[#141414] mb-6">
        <div className="h-full bg-[#CCFF00] transition-all" style={{ width: `${progress.percent}%` }} />
      </div>

      {/* AI decision-support: recap prior coach notes before rating */}
      <div className="mb-6" data-testid="assessment-ai-summary-slot">
        <CoachNotesSummary studentId={studentId} variant="compact" hideWhenEmpty />
      </div>

      <div className="mb-6">
        <LevelJourney completed={state?.completed_levels} focus={state?.focus_areas} waived={state?.waived_skills} audience="coach" />
      </div>

      {template?.tagline && (
        <div className="text-xs text-[#A3A3A3] mb-6 italic">{template.tagline}</div>
      )}

      {/* Multi-level assessment toggle — coach can peek at lower baselines
          (backfill 2.5 competencies) OR higher-level skills the player already
          has (so the DUPR radar reflects reality). Current level is always
          visible; extras render below the current section, dimmer. */}
      {hasHistory || coachConfirmed ? (
        <div className="mb-5" data-testid="level-toggle-row">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className="label-mono text-[10px] text-[#A3A3A3]">SHOW LEVELS</span>
            {ALLOWED.map((lv) => {
              const on = lv === al.current || shownLevels.includes(lv);
              const isCurrent = lv === al.current;
              return (
                <button
                  key={lv}
                  type="button"
                  disabled={isCurrent}
                  onClick={() => toggleLevel(lv)}
                  data-testid={`level-toggle-${lv}`}
                  className="text-[10px] uppercase tracking-wider px-2.5 py-1 border transition-colors"
                  style={on
                    ? { background: isCurrent ? "#CCFF00" : "#141414", color: isCurrent ? "#0A0A0A" : "#CCFF00", borderColor: "#CCFF00", fontWeight: 700 }
                    : { borderColor: "#262626", color: "#A3A3A3" }}
                  title={isCurrent ? "Target level (always shown)" : (on ? `Hide ${lv}` : `Show ${lv} skills`)}
                >
                  {lv}{isCurrent ? " · target" : ""}
                </button>
              );
            })}
            {LEGACY_CONFIRM && (
              <button
                type="button"
                onClick={openAdjust}
                data-testid="open-adjust-level"
                className="ml-auto text-[10px] uppercase tracking-wider px-2.5 py-1 border border-[#FF4500] text-[#FF4500] hover:bg-[#FF4500] hover:text-black inline-flex items-center gap-1"
                title="Move the player up or down a level without wiping their skill ticks"
              >
                <ChevronsUpDown className="w-3 h-3" /> Adjust level
              </button>
            )}
          </div>
          <div className="text-[10px] text-[#525252] leading-relaxed">
            Ticking a lower level lets you backfill baseline competencies. Ticking a higher level lets you flag skills the player already has — both feed the DUPR radar. Only the target level counts toward the promote gate.
          </div>
        </div>
      ) : null}

      <div className="space-y-6">
        {visibleLevels.map((lv) => {
          const tpl = combinedTemplates[lv];
          if (!tpl) return null;
          const isCurrent = lv === al.current;
          const groups = tpl.groups || [];
          return (
            <div
              key={lv}
              data-testid={`level-section-${lv}`}
              className={`${isCurrent ? "" : "opacity-90 border-l-2 border-dashed border-[#262626] pl-4"}`}
            >
              {!isCurrent && (
                <div className="flex items-center justify-between mb-3">
                  <div className="label-mono text-[11px]" style={{ color: "#A3A3A3" }}>
                    {lv} · {LEVEL_LABELS[lv]} · reference only
                  </div>
                  <button
                    type="button"
                    onClick={() => toggleLevel(lv)}
                    className="text-[10px] text-[#525252] hover:text-white uppercase tracking-wider inline-flex items-center gap-1"
                    data-testid={`hide-level-${lv}`}
                  >
                    Hide <X className="w-3 h-3" />
                  </button>
                </div>
              )}
              {groups.map((g) => (
                <div key={`${lv}-${g.group}`} data-testid={`assessment-group-${lv}-${g.group.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>
                  <div className="label-mono mb-3 pb-2 border-b border-[#262626]" style={{ color: isCurrent ? "#CCFF00" : "#525252" }}>{g.group}</div>
                  <div className="space-y-3">
                    {g.skills.map((skill) => {
                      const entry = al.skills[skill.id] || { status: "not_assessed", notes: "" };
                      const pending = staged[skill.id];
                      const effStatus = pending?.status || entry.status;
                      const meta = STATUS_META[effStatus] || STATUS_META.not_assessed;
                      const isOpen = openNotes[skill.id];
                      return (
                        <div key={skill.id} className="border p-3 sm:p-4 bg-[#0A0A0A]" style={{ borderColor: pending ? "#FF9F1C" : "#262626", borderStyle: pending ? "dashed" : "solid" }} data-testid={`skill-row-${skill.id}`}>
                          <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-sm">{skill.name}{pending && <span className="ml-2 text-[9px] uppercase tracking-wider text-[#FF9F1C]" data-testid={`skill-${skill.id}-unsaved`}>Unsaved</span>}</div>
                              <div className="text-xs text-[#A3A3A3] mt-0.5 leading-relaxed">{skill.criteria}</div>
                            </div>
                            <div
                              className="px-2 py-0.5 text-[10px] uppercase tracking-wider shrink-0"
                              style={{ background: meta.color, color: meta.textColor, fontWeight: 700 }}
                              data-testid={`skill-status-${skill.id}`}
                            >
                              {meta.label}
                            </div>
                          </div>

                          <div className="flex flex-wrap gap-2 mt-3">
                            {["competent", "flagged", "waived", "developing", "not_yet", "not_assessed"].map((s) => {
                              const m = STATUS_META[s];
                              const active = effStatus === s;
                              return (
                                <button
                                  key={s}
                                  type="button"
                                  disabled={busy}
                                  onClick={() => setStatus(skill.id, s)}
                                  data-testid={`skill-${skill.id}-set-${s}`}
                                  className="text-[10px] uppercase tracking-wider px-3 py-1.5 border transition-all disabled:opacity-50"
                                  style={active ? { background: m.color, color: m.textColor, borderColor: m.color, fontWeight: 700 } : { borderColor: "#262626", color: "#A3A3A3" }}
                                >
                                  {m.label}
                                </button>
                              );
                            })}
                            <button
                              type="button"
                              onClick={() => { setOpenNotes((cur) => ({ ...cur, [skill.id]: !cur[skill.id] })); setDraftNotes((cur) => (cur[skill.id] === undefined ? { ...cur, [skill.id]: pending?.notes ?? entry.notes ?? "" } : cur)); }}
                              className="text-[10px] uppercase tracking-wider px-3 py-1.5 border border-[#262626] text-[#A3A3A3] hover:text-white"
                              data-testid={`skill-${skill.id}-toggle-notes`}
                            >
                              {isOpen ? "Hide note" : entry.notes ? "Edit note" : "Add note"}
                            </button>
                          </div>

                          {isOpen && (
                            <div className="mt-3">
                              <div className="flex items-center gap-1.5 mb-1.5 text-[10px] uppercase tracking-wider text-[#A3A3A3]" data-testid={`skill-${skill.id}-note-label`}>
                                {(noteIntent[skill.id] || effStatus) === "waived" ? "Reason for waiving" : (noteIntent[skill.id] || effStatus) === "flagged" ? "Reason for flagging" : "Coach note"}
                                <TooltipProvider delayDuration={100}>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <button type="button" aria-label="About this note" className="text-[#737373] hover:text-white" data-testid={`skill-${skill.id}-note-info`}>
                                        <Info className="w-3.5 h-3.5" />
                                      </button>
                                    </TooltipTrigger>
                                    <TooltipContent className="max-w-xs bg-[#141414] border border-[#262626] text-white text-xs normal-case tracking-normal" data-testid={`skill-${skill.id}-note-tooltip`}>
                                      {(noteIntent[skill.id] || effStatus) === "flagged"
                                        ? "Add a note explaining why this skill needs more work. This will be visible to all coaches working with this player."
                                        : "This note will be visible to all coaches working with this player."}
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </div>
                              <textarea
                                value={draftNotes[skill.id] || ""}
                                onChange={(e) => setDraftNotes((cur) => ({ ...cur, [skill.id]: e.target.value }))}
                                rows={2}
                                placeholder="Coach note (e.g., 'Strong forehand drop — needs deeper return placement')"
                                className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-xs focus:outline-none focus:border-[#CCFF00]"
                                data-testid={`skill-${skill.id}-notes-input`}
                              />
                              <button
                                type="button"
                                disabled={busy}
                                onClick={() => saveNotes(skill.id)}
                                className="mt-2 text-[10px] uppercase tracking-wider px-3 py-1.5 bg-[#CCFF00] text-black font-bold disabled:opacity-50"
                                data-testid={`skill-${skill.id}-save-note`}
                              >
                                Add to changes
                              </button>
                            </div>
                          )}

                          {entry.notes && !isOpen && (
                            <div className="mt-2 text-xs text-[#A3A3A3] italic border-l-2 border-[#262626] pl-3">&ldquo;{entry.notes}&rdquo;</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          );
        })}
      </div>

      {/* Staged changes bar */}
      {stagedLines.length > 0 && (
        <div className="sticky bottom-3 z-20 mt-6 p-3 border border-[#FF9F1C] bg-[#0A0A0A]/95 backdrop-blur flex flex-wrap items-center justify-between gap-2" data-testid="staged-changes-bar">
          <span className="text-xs text-[#FF9F1C] font-semibold">{stagedLines.length} unsaved change{stagedLines.length === 1 ? "" : "s"}</span>
          <div className="flex gap-2">
            <button type="button" disabled={busy} onClick={() => { setStaged({}); setDraftNotes({}); setOpenNotes({}); }} className="text-[10px] uppercase tracking-wider px-3 py-1.5 border border-[#404040] text-[#A3A3A3] hover:text-white" data-testid="staged-discard">Discard</button>
            <button type="button" disabled={busy} onClick={() => setReviewOpen(true)} className="text-[10px] uppercase tracking-wider px-3 py-1.5 bg-[#CCFF00] text-black font-bold" data-testid="staged-review-save">Review &amp; save</button>
          </div>
        </div>
      )}
      <ConfirmChangeDialog open={guard.open} testid="unsaved-guard" title="Unsaved changes"
        lines={["You have unsaved changes. If you leave now your changes will be lost. Stay or discard?"]}
        confirmLabel="Discard changes" cancelLabel="Stay" onConfirm={guard.discard} onCancel={guard.stay} />
      <ConfirmChangeDialog open={reviewOpen} busy={busy} testid="skill-review"
        title={`Save ${stagedLines.length} change${stagedLines.length === 1 ? "" : "s"} for ${state?.player_name || "this player"}?`}
        lines={stagedLines} confirmLabel="Save changes" onConfirm={applyStaged} onCancel={() => setReviewOpen(false)} />
      <ConfirmChangeDialog open={promoteOpen} busy={busy} testid="promote"
        title={`Promote ${state?.player_name || "this player"} to ${nxt}?`}
        lines={[
          `You are changing ${state?.player_name || "this player"}'s target level from ${al.current} to ${nxt}.`,
          `${al.current} will be marked ${(progress.flagged || progress.waived) ? "Completed with advisories" : "Completed"}.`,
          ...((progress.flagged || 0) ? [`${progress.flagged} flagged skill${progress.flagged === 1 ? "" : "s"} carry forward as focus areas.`] : []),
        ]}
        confirmLabel={`Promote to ${nxt}`} onConfirm={advance} onCancel={() => setPromoteOpen(false)} />

      {/* Advance button */}
      <div className="mt-6 pt-5 border-t border-[#262626] flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs text-[#A3A3A3]">
          {allCompetent && nxt
            ? `Ready to promote → ${nxt}${(progress.flagged || progress.waived) ? " · level will show as Completed with advisories" : ""}`
            : nxt
              ? `${progress.competent + progress.flagged + progress.waived} / ${progress.total} skills resolved — mark each Competent, Flagged or Waived to promote.`
              : `Player is at the top level (${al.current}).`}
        </div>
        <button
          type="button"
          disabled={busy || !allCompetent || !nxt || stagedLines.length > 0}
          onClick={() => setPromoteOpen(true)}
          data-testid="assessment-advance-btn"
          className="px-5 py-3 text-xs inline-flex items-center gap-2 font-bold uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ background: allCompetent && nxt ? "#CCFF00" : "#262626", color: allCompetent && nxt ? "#0A0A0A" : "#525252" }}
        >
          <ChevronUp className="w-4 h-4" /> Promote to {nxt || "—"}
        </button>
      </div>

      {/* History */}
      {(al.history || []).length > 0 && (
        <div className="mt-6 pt-5 border-t border-[#262626]">
          <div className="label-mono mb-2">LEVEL HISTORY</div>
          <div className="flex flex-wrap gap-2">
            {al.history.map((h, i) => {
              const isAdjust = h.kind === "adjustment" || h.kind === "target_set";
              const label = h.kind === "target_set"
                ? `Target set ${h.set_to}`
                : isAdjust
                ? `${h.level} → ${h.adjusted_to} (${h.direction || "adjusted"})`
                : `${h.level} · ${(h.completed_at || "").slice(0,10)}${h.status === "completed_with_advisories" ? " · advisories" : ""}`;
              return (
                <div
                  key={i}
                  className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider px-2 py-1 border text-[#A3A3A3]"
                  style={{ borderColor: isAdjust ? "#FF4500" : "#262626" }}
                  data-testid={`history-${h.level}${isAdjust ? "-adjust" : ""}`}
                  title={isAdjust ? (h.reason || "adjusted") : ""}
                >
                  {isAdjust
                    ? <ChevronsUpDown className="w-3 h-3 text-[#FF4500]" />
                    : <Check className="w-3 h-3 text-[#CCFF00]" />}
                  {label}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Adjust-level modal */}
      {adjustOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => !busy && setAdjustOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="adjust-level-title"
        >
          <div onClick={(e) => e.stopPropagation()} className="card-surface w-full max-w-md p-6" data-testid="adjust-level-modal">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div id="adjust-level-title" className="label-mono mb-1" style={{ color: "#FF4500" }}>ADJUST LEVEL</div>
                <div className="text-sm text-[#E5E5E5]">Move {state?.player_name || "the player"} up or down without wiping their skill ticks. Existing competent / developing / not-yet marks stay in place.</div>
              </div>
              <button onClick={() => !busy && setAdjustOpen(false)} className="text-[#A3A3A3] hover:text-white" data-testid="adjust-level-close" aria-label="Close adjust level modal">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="label-mono text-[10px] text-[#A3A3A3] mb-2">NEW LEVEL</div>
            <div className="flex flex-wrap gap-2 mb-4">
              {ALLOWED.map((lv) => {
                const on = adjustLevel === lv;
                const isCurrent = lv === al.current;
                return (
                  <button
                    key={lv}
                    type="button"
                    disabled={isCurrent}
                    onClick={() => setAdjustLevel(lv)}
                    data-testid={`adjust-pick-${lv}`}
                    className="px-3 py-2 text-xs uppercase tracking-wider border transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    style={on
                      ? { borderColor: "#CCFF00", background: "rgba(204,255,0,0.08)", color: "#CCFF00", fontWeight: 700 }
                      : { borderColor: "#262626", color: "#E5E5E5" }}
                  >
                    <div className="font-bold">{lv}</div>
                    <div className="text-[9px] text-[#A3A3A3] mt-0.5">
                      {LEVEL_LABELS[lv]}{isCurrent ? " · current" : ""}
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="label-mono text-[10px] text-[#A3A3A3] mb-2">REASON</div>
            <textarea
              rows={3}
              value={adjustReason}
              onChange={(e) => setAdjustReason(e.target.value)}
              placeholder="e.g., Player self-rated 4.5 but returns and resets are firmly at 3.5 level. Working on baseline consistency first."
              className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-xs focus:outline-none focus:border-[#FF4500] mb-4"
              data-testid="adjust-reason-input"
              maxLength={500}
            />
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setAdjustOpen(false)}
                disabled={busy}
                className="text-[11px] uppercase tracking-wider px-4 py-2 border border-[#262626] text-[#A3A3A3] hover:text-white"
                data-testid="adjust-cancel-btn"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitAdjust}
                disabled={busy || !adjustLevel || adjustLevel === al.current || adjustReason.trim().length < 2}
                className="text-[11px] uppercase tracking-wider px-4 py-2 font-bold bg-[#FF4500] text-black disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="adjust-submit-btn"
              >
                {busy ? "Saving…" : `Move to ${adjustLevel || "…"}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
